/**
 * SANCTIONED REFERENCE IMPLEMENTATION (page-layer classification): this page
 * deliberately hand-wires data-grid + filter-bar + crud-editor against the
 * mock users adapter instead of using `createCrudPage` — it demonstrates and
 * exercises the raw module composition the factory automates. Single-consumer,
 * page-specific by design; not a candidate for `features/`.
 *
 * @typedef {import('../contracts/grid.js').ColumnDef} ColumnDef
 * @typedef {import('../contracts/grid.js').RowAction} RowAction
 * @typedef {import('../contracts/grid.js').RowDetailRenderer} RowDetailRenderer
 * @typedef {import('../contracts/filters.js').FilterDef} FilterDef
 * @typedef {import('../contracts/forms.js').FieldDef} FieldDef
 */

import { createUrlState } from '../core/routing/url-state.js';
import { createElement } from '../core/dom/helpers.js';
import { createDataGrid } from '../modules/data-grid/data-grid.js';
import { createFilterBar } from '../modules/filter-bar/filter-bar.js';
import { createCrudEditor } from '../modules/crud-editor/crud-editor.js';
import { createMockUsersAdapter } from '../adapters/mock-users.js';
import { NotFoundError } from '../core/http/errors.js';
import { confirmDialog } from '../components/confirm-dialog/confirm-dialog.js';
import { createFieldRegistry } from '../core/registry/field-registry.js';
import { createFilterRegistry } from '../core/registry/filter-registry.js';
import { textDriver } from '../components/form-field/drivers/text-driver.js';
import { textareaDriver } from '../components/form-field/drivers/textarea-driver.js';
import { selectDriver } from '../components/form-field/drivers/select-driver.js';
import { checkboxDriver } from '../components/form-field/drivers/checkbox-driver.js';
import { dateDriver } from '../components/form-field/drivers/date-driver.js';
import { textFilterDriver } from '../modules/filter-bar/drivers/text-filter-driver.js';
import { selectFilterDriver } from '../modules/filter-bar/drivers/select-filter-driver.js';
import { multiselectFilterDriver } from '../modules/filter-bar/drivers/multiselect-filter-driver.js';
import { dateFilterDriver } from '../modules/filter-bar/drivers/date-filter-driver.js';
import { daterangeFilterDriver } from '../modules/filter-bar/drivers/daterange-filter-driver.js';
import { checkboxFilterDriver } from '../modules/filter-bar/drivers/checkbox-filter-driver.js';
import { radioFilterDriver } from '../modules/filter-bar/drivers/radio-filter-driver.js';
import { timeFilterDriver } from '../modules/filter-bar/drivers/time-filter-driver.js';
import { datetimeFilterDriver } from '../modules/filter-bar/drivers/datetime-filter-driver.js';

/** @type {ColumnDef[]} */
const COLUMNS = [
  { key: 'id', label: 'ID', sortable: true },
  { key: 'name', label: 'Name', sortable: true },
  { key: 'email', label: 'Email', sortable: true },
  {
    key: 'role', label: 'Role', sortable: true,
    render: (value) => String(value).charAt(0).toUpperCase() + String(value).slice(1),
  },
  {
    key: 'status', label: 'Status', sortable: true,
    render: (value) => {
      const s = String(value);
      const badges = /** @type {Record<string, string>} */ ({
        active: '\u2713 Active', inactive: '\u2717 Inactive', pending: '\u23F3 Pending',
      });
      return badges[s] ?? s;
    },
  },
  {
    key: 'created', label: 'Created', sortable: true,
    render: (value) => new Date(String(value)).toLocaleDateString(),
  },
];

/**
 * Mock async loader for roles — demonstrates signal-respecting abort handling.
 * Simulates a 500ms API delay.
 *
 * @type {import('../contracts/forms.js').OptionLoader}
 */
async function mockLoadRoles(ctx) {
  await new Promise((resolve, reject) => {
    const timer = setTimeout(resolve, 500);
    if (ctx.signal) {
      if (ctx.signal.aborted) { clearTimeout(timer); reject(ctx.signal.reason); return; }
      ctx.signal.addEventListener('abort', () => {
        clearTimeout(timer);
        reject(new DOMException('Aborted', 'AbortError'));
      }, { once: true });
    }
  });
  return [
    { label: 'Admin', value: 'admin' },
    { label: 'Editor', value: 'editor' },
    { label: 'Viewer', value: 'viewer' },
  ];
}

/** @type {FilterDef[]} */
const FILTERS = [
  { key: 'search', label: 'Search', type: 'text', placeholder: 'Search by name or email...', span: 4 },
  {
    key: 'role', label: 'Role', type: 'select',
    options: [
      { label: 'Admin', value: 'admin' },
      { label: 'Editor', value: 'editor' },
      { label: 'Viewer', value: 'viewer' },
    ],
    loadOptions: mockLoadRoles,
  },
  {
    key: 'status', label: 'Status', type: 'multiselect',
    options: [
      { label: 'Active', value: 'active' },
      { label: 'Inactive', value: 'inactive' },
      { label: 'Pending', value: 'pending' },
    ],
  },
  { key: 'verified', label: 'Verified only', type: 'checkbox' },
  {
    key: 'tier', label: 'Tier', type: 'radio',
    options: [
      { label: 'Free', value: 'free' },
      { label: 'Pro', value: 'pro' },
      { label: 'Enterprise', value: 'enterprise' },
    ],
  },
];

/** @type {FieldDef[]} */
const FIELDS = [
  { key: 'name', label: 'Name', type: 'text', required: true, placeholder: 'Full name' },
  { key: 'email', label: 'Email', type: 'text', required: true, placeholder: 'Email address' },
  {
    key: 'role', label: 'Role', type: 'select',
    options: [
      { label: 'Admin', value: 'admin' },
      { label: 'Editor', value: 'editor' },
      { label: 'Viewer', value: 'viewer' },
    ],
  },
  {
    key: 'status', label: 'Status', type: 'select',
    options: [
      { label: 'Active', value: 'active' },
      { label: 'Inactive', value: 'inactive' },
      { label: 'Pending', value: 'pending' },
    ],
  },
];

/** @type {RowAction[]} */
const ROW_ACTIONS = [
  { key: 'edit', label: 'Edit' },
  { key: 'delete', label: 'Delete', variant: 'danger' },
];

/**
 * Renders expanded detail content for a user row.
 *
 * @type {RowDetailRenderer}
 */
function renderUserDetail(container, row) {
  const user = /** @type {Record<string, unknown>} */ (row);
  const dl = createElement('dl', { class: 'user-detail' });

  const fields = [
    ['ID', user.id],
    ['Full Name', user.name],
    ['Email', user.email],
    ['Role', String(user.role).charAt(0).toUpperCase() + String(user.role).slice(1)],
    ['Status', String(user.status).charAt(0).toUpperCase() + String(user.status).slice(1)],
    ['Created', new Date(String(user.created)).toLocaleDateString()],
  ];

  for (const [label, value] of fields) {
    dl.appendChild(createElement('dt', {}, [String(label)]));
    dl.appendChild(createElement('dd', {}, [String(value)]));
  }

  container.appendChild(dl);

  return { destroy() { dl.remove(); } };
}

/**
 * Users page mount function.
 *
 * @type {import('../contracts/page.js').PageMountFn}
 */
export default function mountUsersPage(container, services) {
  const eventBus = /** @type {{ on: (name: string, handler: (data: unknown) => void) => () => void, emit: (name: string, data?: unknown) => void }} */ (services.eventBus);
  const logger = /** @type {{ debug: (msg: string, ctx?: object) => void, info: (msg: string, ctx?: object) => void, warn: (msg: string, ctx?: object) => void, error: (msg: string, ctx?: object) => void }} */ (services.logger);
  const urlState = createUrlState({ unorderedKeys: new Set(['status']) });
  const adapter = createMockUsersAdapter({ count: 75 });

  // Page-scoped registries
  const fieldRegistry = createFieldRegistry();
  fieldRegistry.register(textDriver);
  fieldRegistry.register(textareaDriver);
  fieldRegistry.register(selectDriver);
  fieldRegistry.register(checkboxDriver);
  fieldRegistry.register(dateDriver);

  const filterReg = createFilterRegistry();
  filterReg.register(textFilterDriver);
  filterReg.register(selectFilterDriver);
  filterReg.register(multiselectFilterDriver);
  filterReg.register(dateFilterDriver);
  filterReg.register(daterangeFilterDriver);
  filterReg.register(checkboxFilterDriver);
  filterReg.register(radioFilterDriver);
  filterReg.register(timeFilterDriver);
  filterReg.register(datetimeFilterDriver);

  /** @type {Array<() => void>} */
  const unsubscribers = [];

  logger.info('Users page initializing');

  // Build page structure
  const page = createElement('div', { class: 'page-container' });
  const headerRow = createElement('div', { class: 'page-header' });
  const heading = createElement('h1', {}, ['Users']);
  const addBtn = createElement('button', { class: 'btn btn-primary', type: 'button' }, ['Add User']);
  headerRow.appendChild(heading);
  headerRow.appendChild(addBtn);

  // Toast demo buttons — test all four types
  const toastDemoRow = createElement('div', { class: 'toast-demo', style: 'display:flex;gap:8px;padding:8px 0' });
  for (const [type, msg] of /** @type {const} */ ([
    ['success', 'Operation completed successfully'],
    ['error', 'Something went wrong. Please try again.'],
    ['warning', 'Your session will expire in 5 minutes'],
    ['info', 'New version available — refresh to update'],
  ])) {
    const btn = createElement('button', { class: 'btn', type: 'button' }, [`Toast: ${type}`]);
    btn.addEventListener('click', () => {
      eventBus.emit('toast.message.requested', { message: msg, type });
    });
    toastDemoRow.appendChild(btn);
  }

  const filterContainer = createElement('div', { class: 'filter-bar-container' });
  const gridContainer = createElement('div', { class: 'data-grid-container' });
  const editorContainer = createElement('div');

  page.appendChild(headerRow);
  page.appendChild(toastDemoRow);
  page.appendChild(filterContainer);
  page.appendChild(gridContainer);
  page.appendChild(editorContainer);
  container.appendChild(page);

  // Mount filter bar
  const filterBar = createFilterBar(filterContainer, {
    filters: FILTERS,
    urlState,
    logger,
    filterRegistry: filterReg,
  });

  // Selection counter
  const selectionInfo = createElement('div', { class: 'selection-info' });
  selectionInfo.style.display = 'none';
  page.insertBefore(selectionInfo, gridContainer);

  // Mount data grid
  const grid = createDataGrid(gridContainer, {
    columns: COLUMNS,
    fetchPage: (/** @type {import('../contracts/grid.js').GridQuery} */ query, /** @type {AbortSignal | undefined} */ signal) => adapter.fetchPage(query, signal),
    urlState,
    logger,
    onError: (/** @type {Error} */ err) => {
      logger.error('Grid error', { data: err });
    },
    onRowAction: handleRowAction,
    rowActions: ROW_ACTIONS,
    rowKey: 'id',
    pageSizeOptions: [10, 25, 50],
    renderDetail: renderUserDetail,
    selectable: true,
    onSelectionChange: (/** @type {unknown[]} */ selected) => {
      if (selected.length > 0) {
        selectionInfo.textContent = `${selected.length} user${selected.length > 1 ? 's' : ''} selected`;
        selectionInfo.style.display = '';
      } else {
        selectionInfo.style.display = 'none';
      }
    },
    stickyHeader: true,
    mobileMode: 'responsive',
  });

  // Mount crud editor
  const editor = createCrudEditor(editorContainer, {
    fields: FIELDS,
    adapter,
    logger,
    eventBus,
    fieldRegistry,
    localeConfig: services.localeConfig,
    onSaved: () => grid.refresh(),
    onError: (/** @type {Error} */ err) => {
      logger.error('Editor error', { data: err });
    },
  });

  // "Add User" button — cleaned up via page DOM removal + explicit handler ref
  const onAddClick = () => editor.open();
  addBtn.addEventListener('click', onAddClick);
  unsubscribers.push(() => addBtn.removeEventListener('click', onAddClick));

  /**
   * @param {string} action
   * @param {unknown} row
   */
  function handleRowAction(action, row) {
    const record = /** @type {Record<string, unknown>} */ (row);
    if (action === 'edit') {
      editor.open(/** @type {number} */ (record.id));
    } else if (action === 'delete') {
      const name = record.name ? ` "${record.name}"` : '';
      confirmDialog({
        title: 'Delete user',
        message: `Are you sure you want to delete user${name}? This action cannot be undone.`,
      }).then((confirmed) => {
        if (!confirmed) return;
        adapter.delete(/** @type {number} */ (record.id))
          .then(() => {
            grid.refresh();
            eventBus.emit('toast.message.requested', {
              message: 'User deleted',
              type: 'success',
            });
          })
          .catch((/** @type {Error} */ err) => {
            if (err instanceof NotFoundError) {
              grid.refresh();
              return;
            }
            logger.error('Delete failed', { data: err });
            eventBus.emit('toast.message.requested', {
              message: 'Failed to delete user',
              type: 'error',
            });
          });
      });
    }
  }

  logger.info('Users page initialized');

  function destroy() {
    for (const unsub of unsubscribers) unsub();
    unsubscribers.length = 0;

    editor.destroy();
    grid.destroy();
    filterBar.destroy();
    page.remove();
  }

  return { destroy };
}
